/**
 * GuideHud — floating field-navigation chip over the map.
 *
 * Shows the guide target's label plus live distance / compass direction from
 * the user's GPS position; turns green on arrival (within GPS accuracy).
 * The guide stays until the user cancels or picks a new target — auto-dismiss
 * under GPS jitter is confusing. Renders nothing when no guide is active.
 */

import { useMapStore } from '../../stores';
import { haversineM, bearingDeg, compassSector } from '../../utils/geometry';

export default function GuideHud() {
  const guideTarget = useMapStore((s) => s.guideTarget);
  const userLocation = useMapStore((s) => s.userLocation);
  const clearGuideTarget = useMapStore((s) => s.clearGuideTarget);

  if (!guideTarget) return null;

  let distanceText = 'waiting for GPS…';
  let arrived = false;
  if (userLocation) {
    const from: [number, number] = [userLocation.lng, userLocation.lat];
    const to: [number, number] = [guideTarget.lng, guideTarget.lat];
    const d = haversineM(from, to);
    arrived = d <= Math.max(userLocation.accuracy, 3);
    if (arrived) {
      distanceText = "You're here";
    } else {
      const dist = d >= 1000 ? `${(d / 1000).toFixed(2)} km` : `${Math.round(d)} m`;
      distanceText = `${dist} ${compassSector(bearingDeg(from, to))}`;
    }
  }

  return (
    <div className={`guide-hud${arrived ? ' guide-hud--arrived' : ''}`} data-testid="guide-hud">
      <span className="guide-hud__label">→ {guideTarget.label || 'Target'}</span>
      <span className="guide-hud__distance" data-testid="guide-hud-distance">{distanceText}</span>
      <button
        className="guide-hud__close"
        onClick={clearGuideTarget}
        title="Stop guiding"
        aria-label="Stop guiding"
      >
        ✕
      </button>
    </div>
  );
}
